import axios from "axios";
import { getAccessToken } from "../utils/session";

const rawBaseUrl = (process.env.REACT_APP_API_BASE_URL || "http://localhost:3000").trim();
const normalizedBase = rawBaseUrl.endsWith("/") ? rawBaseUrl.slice(0, -1) : rawBaseUrl;

export const API_BASE_URL = normalizedBase;
export const API_PREFIX = "/v1";

export const http = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  timeout: 20000,
});

export const INVALID_TOKEN_EVENT = "ieee:invalid-token";

let activeHttpRequests = 0;
let invalidTokenHandled = false;
const loadingSubscribers = new Set();

const notifyLoadingSubscribers = () => {
  loadingSubscribers.forEach((callback) => {
    try {
      callback(activeHttpRequests);
    } catch {
      // Ignore subscriber callback failures.
    }
  });
};

const trackRequestStart = (config) => {
  const skipLoaderHeader = config?.headers?.get?.("X-Skip-Loader");
  const skipLoader =
    skipLoaderHeader ??
    config?.headers?.["X-Skip-Loader"] ??
    config?.headers?.["x-skip-loader"];

  if (!skipLoader) {
    activeHttpRequests += 1;
    config.__trackGlobalLoader = true;
    notifyLoadingSubscribers();
  }

  return config;
};

const trackRequestEnd = (config) => {
  if (config?.__trackGlobalLoader) {
    activeHttpRequests = Math.max(0, activeHttpRequests - 1);
    notifyLoadingSubscribers();
  }
};

const isInvalidTokenError = (error) => {
  const status = error?.response?.status;
  const requestUrl = error?.config?.url || "";
  const message =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "";

  if (status !== 401) return false;
  if (!getAccessToken()) return false;
  if (requestUrl.includes("/auth/login") || requestUrl.includes("/auth/logout")) {
    return false;
  }

  return /invalid token|token invalid|jwt|unauthorized|expired token|token expired/i.test(message) || status === 401;
};

const notifyInvalidToken = (error) => {
  if (invalidTokenHandled || typeof window === "undefined") return;
  invalidTokenHandled = true;
  window.dispatchEvent(
    new CustomEvent(INVALID_TOKEN_EVENT, {
      detail: {
        message:
          error?.response?.data?.message ||
          "Your session has expired. Please log in again.",
      },
    })
  );
};

http.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return trackRequestStart(config);
}, (error) => {
  trackRequestEnd(error?.config);
  return Promise.reject(error);
});

http.interceptors.response.use((response) => {
  trackRequestEnd(response?.config);
  return response;
}, (error) => {
  trackRequestEnd(error?.config);
  if (isInvalidTokenError(error)) {
    notifyInvalidToken(error);
  }
  return Promise.reject(error);
});

export function getApiErrorMessage(error, fallback = "Request failed") {
  if (error?.response?.data?.message) return error.response.data.message;
  if (error?.response?.data?.error) return error.response.data.error;
  if (error?.message) return error.message;
  return fallback;
}

export function getActiveHttpRequests() {
  return activeHttpRequests;
}

export function subscribeHttpLoading(callback) {
  if (typeof callback !== "function") return () => {};
  loadingSubscribers.add(callback);
  callback(activeHttpRequests);
  return () => loadingSubscribers.delete(callback);
}

export function resetInvalidTokenHandler() {
  invalidTokenHandled = false;
}
